import { AfterViewInit, ChangeDetectionStrategy, Component, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSort, MatSortable } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { debounceTime, filter, map, take } from 'rxjs/operators';
import { LmoFracsService } from '~lmo/services';
import { LmoFracMineService } from '~lmo/services/lmo-frac-mine.service';
import { LmoLoadersService } from '~lmo/services/lmo-loaders.service';

interface MineInfo {
  id: number;
  name: string;
  distance: number;
}

interface MineSummaryInfo extends MineInfo {
  waitTime: number;
  updatedTimestamp: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'sa-lmo-fcpo-add-loader-to-network',
  styleUrls: ['./lmo-fcpo-add-loader-to-network.component.scss'],
  templateUrl: './lmo-fcpo-add-loader-to-network.component.html',
})
export class LmoFcpoAddLoaderToNetworkComponent implements OnInit, AfterViewInit {
  public search: FormControl = new FormControl();
  public selectedMine: MineSummaryInfo;
  public selectedMineLoading$$ = new BehaviorSubject<boolean>(false);
  public loading$$ = new BehaviorSubject<boolean>(true);
  public dataSource: MatTableDataSource<MineInfo>;
  public displayedColumns = ['name', 'distance'];
  @ViewChild(MatSort, { static: true }) public sort: MatSort;

  constructor(
    private fracMineService: LmoFracMineService,
    private fracService: LmoFracsService,
    private loaderService: LmoLoadersService,
    private dialogRef: MatDialogRef<any, any>,
  ) {}

  public ngOnInit() {}

  public ngAfterViewInit(): void {
    combineLatest(this.fracMineService.getOutOfNetworkLoaders$(), this.fracService.currentFrac$)
      .pipe(
        filter((results) => results.every((result) => !!result)),
        take(1),
        map(([mines, frac]) => {
          return mines.map((mine) => ({
            distance: distance(frac.site.lngLat[1], frac.site.lngLat[0], mine.site.lngLat[1], mine.site.lngLat[0]),
            id: mine.id,
            name: mine.site.name,
          }));
        }),
      )
      .subscribe((result) => {
        this.sort.sort({ id: 'distance', start: 'asc' } as MatSortable);
        this.dataSource = new MatTableDataSource(result);
        this.dataSource.sort = this.sort;
        this.loading$$.next(false);
      });

    this.search.valueChanges.pipe(debounceTime(200)).subscribe((value) => {
      this.dataSource.filter = value;
    });
  }

  public select(row: MineInfo) {
    this.selectedMineLoading$$.next(true);
    combineLatest(this.loaderService.getSingleLoader$(row.id), this.fracService.currentFrac$).subscribe(
      ([mine, frac]) => {
        this.selectedMine = {
          distance: distance(frac.site.lngLat[1], frac.site.lngLat[0], mine.site.lngLat[1], mine.site.lngLat[0]),
          id: mine.id,
          name: mine.site.name,
          updatedTimestamp: mine.updatedTimestamp,
          waitTime: mine.waitTime,
        };
        this.selectedMineLoading$$.next(false);
      },
    );
  }

  public addToNetwork() {
    this.fracMineService.addContractMine$(this.selectedMine.id).subscribe((success) => {
      if (success) {
        this.dialogRef.close(this.selectedMine);
      }
    });
  }
}

// From https://stackoverflow.com/a/21623206/4937673
function distance(lat1, lon1, lat2, lon2): number {
  const p = 0.017453292519943295; // Math.PI / 180
  const c = Math.cos;
  const a = 0.5 - c((lat2 - lat1) * p) / 2 + (c(lat1 * p) * c(lat2 * p) * (1 - c((lon2 - lon1) * p))) / 2;

  return 12742 * Math.asin(Math.sqrt(a)) * 0.62137119; // 2 * R; R = 6371 km; In miles
}
